import { createFileRoute, notFound } from '@tanstack/react-router';
import { BlogPost } from '@/components/astro/blog/BlogPost';
import { BlogContent } from '@/components/astro/blog/blog-content';
import { normalizeCanonicalUrl } from '@/seo/canonical';
import { seo } from '@/seo/seo';
import { blogPostAtom } from '@/lib/blog-atoms';
import {
  successOrThrow,
  useLoadedValue,
} from '@listening/frontend/query/route-loader';
import { cacheHeadersFor, experimentalRoutes } from '@/analytics';

// stale-if-error keeps a working page in front of users when WordPress upstream
// is timing out, so a WP Engine origin blip doesn't surface as a 500.
const BLOG_POST_CACHE_CONTROL =
  'public, max-age=0, s-maxage=3600, stale-while-revalidate=86400, stale-if-error=86400';

const blogPostRouteAnalytics = experimentalRoutes['/_marketing/blog/$slug'];

export const Route = createFileRoute('/_marketing/blog/$slug')({
  staticData: { analytics: blogPostRouteAnalytics },
  headers: cacheHeadersFor(blogPostRouteAnalytics, BLOG_POST_CACHE_CONTROL),
  loader: async ({ context: { atoms }, params }) => {
    const post = await atoms.ensure(blogPostAtom, params.slug);
    const { oldPostFormat } = successOrThrow(post, {
      NotFoundError: () => notFound(),
    }).detailPage;
    return {
      post,
      seo: {
        title: oldPostFormat.title,
        seoDescription: oldPostFormat.seoDescription,
        excerpt: oldPostFormat.excerpt,
        heroImage: oldPostFormat.heroImage,
      },
    };
  },
  head: ({ loaderData, params }) => {
    const post = loaderData?.seo;
    const title = post?.title ?? 'Blog';
    const seoDesc = post?.seoDescription;
    const rawExcerpt = post?.excerpt ?? '';
    const excerptText = rawExcerpt
      ? rawExcerpt
          .replaceAll(/<[^>]+>/g, ' ')
          .replaceAll(/\s+/g, ' ')
          .trim()
      : '';
    const limitedExcerpt = excerptText ? excerptText.slice(0, 155) : '';
    const description = seoDesc ?? limitedExcerpt ?? 'Listening Blog Article';

    const heroImage = post?.heroImage;

    return {
      meta: [
        ...seo({
          title,
          description,
          image: heroImage ?? undefined,
          ogType: 'article',
        }),
      ],
      links: [
        {
          rel: 'canonical',
          href: normalizeCanonicalUrl(`/blog/${params.slug}`),
        },
      ],
    };
  },
  component: Page,
});

function Page() {
  const { detailPage, randomPosts, randomDocs } = useLoadedValue(
    blogPostAtom,
    Route.useLoaderData().post,
  );
  return (
    <BlogPost
      post={detailPage.oldPostFormat}
      randomDocs={randomDocs}
      randomPosts={randomPosts}
    >
      <BlogContent detailPage={detailPage} />
    </BlogPost>
  );
}
